// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * The change form's warning that submitting puts a record beyond correcting (§8.6.3).
 *
 * A record can be edited or revoked only while nothing has been recorded after it. A new
 * change versions this archive and every archive it is fitted in, so the record that
 * brought about the current version of any of them — still in its window — would be left
 * beneath it. That happened silently: the 编辑 button was simply gone afterwards. This says
 * so before, and leads to the record, whose mistake is better put right in place. It does
 * not hold the submit: a later change is often exactly what is meant.
 */

import { useQueries } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { api } from "@/api/endpoints";
import type { MaintenanceEvent } from "@/api/types";

export function PendingCorrectionNotice({
  archiveIds,
  onOpen,
}: {
  /** The archive being changed, then each one it is fitted in, up to the top. */
  archiveIds: string[];
  /** Following the link to a record: the form is left for it. */
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const lists = useQueries({
    queries: archiveIds.map((archiveId) => ({
      queryKey: ["maintenance", archiveId, "correctable"],
      queryFn: () => api.maintenance.list(archiveId, { correctable: true }),
    })),
  });

  // A change inside a module is listed on the module and on every archive above it.
  const seen = new Set<string>();
  const pending: { owner: string; event: MaintenanceEvent }[] = [];
  lists.forEach((list, index) => {
    for (const event of list.data?.items ?? []) {
      if (seen.has(event.event_id)) continue;
      seen.add(event.event_id);
      pending.push({ owner: event.archive_id ?? archiveIds[index] ?? "", event });
    }
  });
  if (pending.length === 0) return null;

  return (
    <div className="banner banner-info" role="status">
      {pending.map(({ owner, event }) => (
        <p key={event.event_id}>
          {t("maintenance.pendingCorrection", {
            n: event.sequence_no,
            archive: event.archive_no ? `${event.archive_no} ` : "",
            time: new Date(event.editable_until ?? "").toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
          })}{" "}
          <Link to={`/archives/${owner}/maintenance/${event.event_id}`} onClick={onOpen}>
            {t("maintenance.pendingCorrectionOpen", { n: event.sequence_no })}
          </Link>
        </p>
      ))}
    </div>
  );
}
